import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  X,
  Brain,
  Trophy,
  BookOpen,
  ChevronRight,
  ChevronLeft,
  Sparkles,
  Target,
  Clock,
  CheckCircle,
  Rocket,
  Layout,
  ArrowRight
} from 'lucide-react';
import twygsLearnLogo from '../../../assets/clay_twygs_logo_no_bg.png';

interface TutorialModalProps {
  isOpen: boolean;
  onClose: () => void;
  onComplete: () => void;
}

interface TutorialStep {
  id: number;
  icon: React.ReactNode;
  useLogo?: boolean;
  title: string;
  subtitle: string;
  description: string;
  highlight?: string;
  color: string;
  bgGradient: string;
  showNavButtons?: boolean;
}

// Twygs Learning color palette
const colors = {
  sage: { main: '#798872', light: '#93A089', dark: '#63705C', bg: '#F5F6F4' },
  clay: { main: '#B87968', light: '#C89483', dark: '#A05D4E', bg: '#FAF5F3' },
  honey: { main: '#BF954F', light: '#CFAD70', dark: '#A67C3E', bg: '#FBF8F2' },
  coral: { main: '#D17B69', light: '#DF9888', dark: '#B85F4D', bg: '#FDF6F4' },
  sky: { main: '#6B9AB3', light: '#88B1C8', dark: '#567D93', bg: '#F4F7F9' }
};

const tutorialSteps: TutorialStep[] = [
  {
    id: 1,
    icon: <Brain className="w-12 h-12" />,
    useLogo: true,
    title: 'Knowledge is Power',
    subtitle: 'Welcome to',
    description:
      'Transform your scroll time into growth time. Our bite-sized learning modules help you become more informed, more interesting, and more capable—all in just a few minutes.',
    highlight: 'The most interesting people never stop learning.',
    color: 'text-[#7F4A3F]',
    bgGradient: 'from-[#B87968] to-[#A05D4E]'
  },
  {
    id: 2,
    icon: <Clock className="w-12 h-12" />,
    title: '2-5 Minutes',
    subtitle: 'Perfect for your busy life',
    description:
      "Each learning module is designed to fit perfectly into your day. While you're waiting for coffee, on your commute, or taking a quick break—that's all the time you need.",
    highlight: 'Small moments, big growth.',
    color: 'text-[#567D93]',
    bgGradient: 'from-[#6B9AB3] to-[#567D93]'
  },
  {
    id: 3,
    icon: <BookOpen className="w-12 h-12" />,
    title: 'Interactive & Engaging',
    subtitle: 'Learning that sticks',
    description:
      "Our modules use visual cards, timelines, comparisons, and interactive elements to make learning memorable. Each one ends with a quick quiz to reinforce what you've learned.",
    highlight: 'Not boring lectures. Real engagement.',
    color: 'text-[#A67C3E]',
    bgGradient: 'from-[#BF954F] to-[#A67C3E]'
  },
  {
    id: 4,
    icon: <Target className="w-12 h-12" />,
    title: 'Topics You Care About',
    subtitle: 'From media literacy to science',
    description:
      'Critical thinking, civics, technology, health, finance—explore topics that matter. Build skills that help you navigate the modern world with confidence.',
    highlight: 'Become the person who knows things.',
    color: 'text-[#A05D4E]',
    bgGradient: 'from-[#B87968] to-[#A05D4E]'
  },
  {
    id: 5,
    icon: <Trophy className="w-12 h-12" />,
    title: 'Earn Seeds',
    subtitle: 'Get rewarded for learning',
    description:
      "Complete modules and pass quizzes to earn seeds—Twygs' reward currency. Use them to support creators, unlock features, and more. Learning literally pays off!",
    highlight: 'Your brain gets smarter. Your wallet gets seeds.',
    color: 'text-[#B85F4D]',
    bgGradient: 'from-[#D17B69] to-[#B85F4D]'
  },
  {
    id: 6,
    icon: <Sparkles className="w-12 h-12" />,
    title: 'Your Learning Dashboard',
    subtitle: 'Take control of your journey',
    description:
      'Visit the Learning page to browse all available series, add them to your personal Learning Path, and track your progress. The more series you add, the more modules will appear in your feed!',
    highlight: 'Curate your own learning adventure.',
    color: 'text-[#567D93]',
    bgGradient: 'from-[#6B9AB3] to-[#456476]'
  },
  {
    id: 7,
    icon: <Rocket className="w-12 h-12" />,
    title: "You're Ready!",
    subtitle: 'Start your learning journey',
    description:
      'Learning modules will appear in your feed as you scroll. You can also visit your Learning Dashboard anytime to discover new series and manage your paths.',
    highlight: 'The journey of a thousand insights begins with a single module.',
    color: 'text-[#63705C]',
    bgGradient: 'from-[#798872] to-[#4F594B]',
    showNavButtons: true
  }
];

const TutorialModal: React.FC<TutorialModalProps> = ({ isOpen, onClose, onComplete }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const navigate = useNavigate();
  const step = tutorialSteps[currentStep];
  const isLastStep = currentStep === tutorialSteps.length - 1;

  // Prevent background scrolling when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const handleNext = () => {
    if (currentStep < tutorialSteps.length - 1) {
      setCurrentStep((prev) => prev + 1);
    } else {
      onComplete();
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep((prev) => prev - 1);
    }
  };

  const handleSkip = () => {
    onComplete();
  };

  const handleGoToLearning = () => {
    onComplete();
    navigate('/learning');
  };

  if (!isOpen) return null;

  const modalContent = (
    <div
      className="fixed inset-0 bg-black/60 flex items-center justify-center z-[999999] p-4"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        width: '100vw',
        height: '100vh'
      }}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.2 }}
        className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[85vh] overflow-hidden flex flex-col"
      >
        {/* Header with gradient and close button */}
        <div
          className={`relative bg-gradient-to-br ${step.bgGradient} px-6 py-4 flex justify-between items-start`}
        >
          <div className="flex-1">
            {/* Step indicator */}
            <div className="flex gap-1.5 mb-2">
              {tutorialSteps.map((_, idx) => (
                <div
                  key={idx}
                  className={`h-1.5 rounded-full transition-all duration-300 ${
                    idx <= currentStep ? 'w-6 bg-white' : 'w-2 bg-white/30'
                  }`}
                />
              ))}
            </div>
            <span className="text-white/80 text-sm font-medium">{step.subtitle}</span>
          </div>
          <button
            onClick={onClose}
            className="text-white/80 hover:text-white transition-colors p-1"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Icon or Logo */}
          <div className="text-center mb-4">
            {step.useLogo ? (
              <img src={twygsLearnLogo} alt="Twygs Learn" className="h-12 w-auto mx-auto" />
            ) : (
              <div
                className={`inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br ${step.bgGradient} text-white`}
              >
                {step.icon}
              </div>
            )}
          </div>

          {/* Title */}
          <h2 className="text-xl font-bold text-gray-800 text-center mb-4">{step.title}</h2>

          {/* Description */}
          <p className="text-gray-600 text-center leading-relaxed mb-4">{step.description}</p>

          {/* Highlight quote */}
          {step.highlight && (
            <div
              className={`text-center p-4 bg-gray-50 rounded-xl border-l-4 border-current ${step.color}`}
            >
              <p className={`font-semibold italic ${step.color}`}>"{step.highlight}"</p>
            </div>
          )}
        </div>

        {/* Fixed Navigation Footer */}
        <div className="flex-shrink-0 border-t bg-gray-50 px-6 py-4">
          {isLastStep && step.showNavButtons ? (
            // Final step with special navigation options
            <div className="space-y-2">
              <button
                onClick={handleGoToLearning}
                className="w-full flex items-center justify-center gap-2 px-6 py-3 rounded-xl font-semibold text-white bg-gradient-to-r from-[#6B9AB3] to-[#567D93] shadow-md hover:shadow-lg transition-all"
              >
                <Layout className="w-5 h-5" />
                Explore Learning Dashboard
                <ArrowRight className="w-5 h-5" />
              </button>

              <button
                onClick={onComplete}
                className={`w-full flex items-center justify-center gap-2 px-6 py-3 rounded-xl font-semibold text-white bg-gradient-to-r ${step.bgGradient} shadow-md hover:shadow-lg transition-all`}
              >
                <CheckCircle className="w-5 h-5" />
                Start Learning in My Feed
              </button>

              <button
                onClick={handlePrev}
                className="w-full flex items-center justify-center gap-1 px-4 py-2 text-gray-500 hover:text-gray-700 text-sm transition-colors"
              >
                <ChevronLeft className="w-4 h-4" />
                Go back
              </button>
            </div>
          ) : (
            // Regular navigation
            <div className="flex justify-between items-center">
              <button
                onClick={handlePrev}
                className={`flex items-center text-gray-600 font-medium py-2 px-4 rounded-lg transition hover:bg-gray-100 ${
                  currentStep === 0 ? 'invisible' : ''
                }`}
              >
                <ChevronLeft className="w-4 h-4 mr-1" />
                Back
              </button>

              <button
                onClick={handleSkip}
                className="text-gray-400 hover:text-gray-600 text-sm transition-colors"
              >
                Skip
              </button>

              <button
                onClick={handleNext}
                className={`flex items-center gap-1 px-6 py-2 rounded-lg font-semibold text-white bg-gradient-to-r ${step.bgGradient} shadow-md hover:shadow-lg transition-all`}
              >
                Next
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default TutorialModal;
